import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  ClinicalNote,
  SessionSchedule,
  deleteClinicalNote,
  getApiErrorMessage,
  getClinicalNotes,
  getCollaboratorSessions,
} from "src/apis/prontuario";
import { Id } from "src/utils/types";
import ClinicalNoteForm from "./ClinicalNoteForm";
import {
  formatClinicalDate,
  formatEmpty,
  formatSessionLabel,
  getClinicalNoteId,
  getNoteSelectionId,
  getSessionSelectionId,
  getStatusColor,
  noteTypeLabels,
} from "./utils";
import SkeletonLoad from "../SkeletonLoad";

type ClinicalNotesListProps = {
  prontuarioId: Id;
  idColaborador: Id;
  onNotify: (message: string, severity?: "success" | "error" | "info" | "warning") => void;
};

const previewText = (value?: string): string => {
  const normalizedValue = formatEmpty(value);

  if (normalizedValue === "-") {
    return "-";
  }

  return normalizedValue.length > 220
    ? `${normalizedValue.slice(0, 220).trim()}...`
    : normalizedValue;
};

const ClinicalNotesList: FC<ClinicalNotesListProps> = ({ prontuarioId, idColaborador, onNotify }) => {
  const [notes, setNotes] = useState<ClinicalNote[]>([]);
  const [sessionsMap, setSessionsMap] = useState<Map<string, SessionSchedule>>(new Map());
  const [loading, setLoading] = useState<boolean>(false);
  const [formOpen, setFormOpen] = useState<boolean>(false);
  const [editingNote, setEditingNote] = useState<ClinicalNote | null>(null);

  const visibleNotes = useMemo(
    () => notes.filter((note) => note.status !== "deletada"),
    [notes]
  );

  const fetchNotes = useCallback(async () => {
    if (!prontuarioId) return;
    setLoading(true);
    try {
      const [notesResponse, sessionsResponse] = await Promise.all([
        getClinicalNotes(prontuarioId),
        getCollaboratorSessions(idColaborador),
      ]);
      setNotes(notesResponse);
      const map = new Map<string, SessionSchedule>();
      for (const session of sessionsResponse) {
        const selectionId = getSessionSelectionId(session);
        if (selectionId) map.set(selectionId, session);
      }
      setSessionsMap(map);
    } catch (err: unknown) {
      onNotify(
        getApiErrorMessage(err, "Não foi possível carregar anotações clínicas."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [prontuarioId, idColaborador, onNotify]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const closeForm = () => {
    setFormOpen(false);
    setEditingNote(null);
  };

  const handleDelete = async (note: ClinicalNote) => {
    const noteId = getClinicalNoteId(note);

    if (!noteId) {
      onNotify("Não foi possível identificar a anotação clínica.", "error");
      return;
    }

    if (!window.confirm("Tem certeza que deseja excluir esta anotação clínica?")) {
      return;
    }

    setLoading(true);

    try {
      await deleteClinicalNote(noteId);
      setNotes((previousNotes) =>
        previousNotes.filter((currentNote) => getClinicalNoteId(currentNote) !== noteId)
      );
      onNotify("Anotação clínica excluída.", "success");
    } catch (err: unknown) {
      onNotify(
        getApiErrorMessage(err, "Não foi possível excluir a anotação clínica."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setFormOpen(true)}
        >
          Nova anotação
        </Button>
      </Box>

      {loading ? (
        <SkeletonLoad variant="cards" rows={3} />
      ) : visibleNotes.length === 0 ? (
        <Alert severity="info">Nenhuma anotação clínica registrada.</Alert>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {visibleNotes.map((note) => {
            const noteType = String(note.tipo_anotacao ?? "observacao");
            const riskValue = note.risco_nota ? `Risco ${note.risco_nota}` : "Risco -";
            const linkedSession = sessionsMap.get(getNoteSelectionId(note));

            return (
              <Card variant="outlined" key={getClinicalNoteId(note)}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {formatEmpty(note.titulo) !== "-" ? note.titulo : "Anotação clínica"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatClinicalDate(note.data_atendimento)} ·{" "}
                        {noteTypeLabels[noteType as keyof typeof noteTypeLabels] ??
                          formatEmpty(noteType)}
                      </Typography>
                      {linkedSession && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Vinculada a: {formatSessionLabel(linkedSession)}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      size="small"
                      color={getStatusColor(note.status)}
                      label={formatEmpty(note.status ?? "ativa")}
                    />
                  </Box>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
                    {(note.tags ?? []).map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                  </Box>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
                    <Chip size="small" label={`Humor ${note.humor_nota ?? "-"}`} />
                    <Chip size="small" label={`Ansiedade ${note.ansiedade_nota ?? "-"}`} />
                    <Chip size="small" label={`Adesão ${note.adesao_nota ?? "-"}`} />
                    <Chip size="small" color={getStatusColor(riskValue)} label={riskValue} />
                    <Chip size="small" label={`Evolução ${note.evolucao_nota ?? "-"}`} />
                  </Box>

                  <Typography variant="body2">
                    {previewText(note.observacao_clinica ?? note.queixa_relato)}
                  </Typography>
                </CardContent>

                <CardActions sx={{ justifyContent: "flex-end" }}>
                  <Tooltip title="Editar">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingNote(note);
                        setFormOpen(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(note)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            );
          })}
        </Box>
      )}

      <Dialog open={formOpen} onClose={closeForm} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingNote ? "Editar anotação clínica" : "Nova anotação clínica"}
        </DialogTitle>
        <DialogContent>
          <ClinicalNoteForm
            prontuarioId={prontuarioId}
            idColaborador={idColaborador}
            note={editingNote}
            onCancel={closeForm}
            onNotify={onNotify}
            onSaved={async () => {
              closeForm();
              await fetchNotes();
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ClinicalNotesList;
